import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { LocationInput } from "@/components/LocationInput";
import { MapPicker } from "@/components/MapPicker";
import { ImageUpload } from "@/components/ImageUpload";
import { PACKAGE_CATEGORIES, PACKAGE_SIZES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Loader2, CheckCircle2, ArrowRight, ArrowLeft, Copy, Wallet } from "lucide-react";

const empty = {
  pickup: "", destination: "", description: "", category: "", size: "", weight: "",
  delivery_date: "", notes: "", pickup_lat: null, pickup_lng: null, dest_lat: null, dest_lng: null,
  package_photo_id: null,
};

export default function CreateDelivery() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 form, 2 review, 3 done
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState(null);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    const { category, weight, pickup_lat, pickup_lng, dest_lat, dest_lng } = form;
    if (!category || !weight || pickup_lat == null || dest_lat == null) { setQuote(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/pricing/quote", { category, weight, pickup_lat, pickup_lng, dest_lat, dest_lng });
        setQuote(data.quote);
      } catch { setQuote(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.category, form.weight, form.pickup_lat, form.pickup_lng, form.dest_lat, form.dest_lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid = form.pickup && form.destination && form.description && form.category && form.size && form.weight && form.delivery_date;

  const toReview = (e) => {
    e.preventDefault();
    if (!valid) { toast.error("Please complete all required fields"); return; }
    if (form.pickup.trim().toLowerCase() === form.destination.trim().toLowerCase()) { toast.error("Pickup and destination cannot be the same"); return; }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (new Date(form.delivery_date) < today) { toast.error("Delivery date cannot be in the past"); return; }
    const m = (form.weight || "").match(/[\d.]+/);
    if (!m || parseFloat(m[0]) <= 0) { toast.error("Weight must be greater than zero"); return; }
    setStep(2);
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/deliveries", form);
      setCreated(data.delivery);
      setStep(3);
      toast.success("Delivery request created successfully.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  if (step === 3 && created) {
    return (
      <div className="max-w-lg mx-auto text-center py-10" data-testid="delivery-confirmation">
        <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto"><CheckCircle2 className="w-9 h-9 text-emerald-500" /></div>
        <h1 className="mt-5 text-2xl font-bold text-slate-900">Delivery request created!</h1>
        <p className="mt-2 text-slate-500">Your request is now live. Travellers heading your way can offer to deliver it.</p>
        <div className="mt-5 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5">
          <span className="text-xs text-slate-400">Delivery ID</span>
          <span className="font-mono font-semibold text-slate-800" data-testid="created-delivery-id">{created.id}</span>
          <button onClick={() => { navigator.clipboard.writeText(created.id); toast.success("Copied"); }}><Copy className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
          <Button onClick={() => navigate("/my-deliveries")} data-testid="view-my-deliveries-btn" className="rounded-full">Go to My Deliveries</Button>
          <Button variant="outline" onClick={() => navigate("/find-travellers")} className="rounded-full">Find a traveller</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Send a Package" subtitle="Tell us what you'd like to send and where it needs to go." />

      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex gap-3 mb-6" data-testid="prohibited-warning">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <strong>Prohibited &amp; illegal items are not allowed.</strong> Do not request the transport of drugs, weapons, cash, hazardous or counterfeit goods. Requests containing prohibited items will be blocked.
        </div>
      </div>

      {step === 1 && (
        <form onSubmit={toReview} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm" data-testid="create-delivery-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <LocationInput label="Pickup Location" required testid="delivery-pickup-input" value={form.pickup}
              onChange={({ text, lat, lng }) => setForm((f) => ({ ...f, pickup: text, pickup_lat: lat ?? null, pickup_lng: lng ?? null }))} />
            <LocationInput label="Destination" required testid="delivery-destination-input" value={form.destination}
              onChange={({ text, lat, lng }) => setForm((f) => ({ ...f, destination: text, dest_lat: lat ?? null, dest_lng: lng ?? null }))} />
          </div>

          <MapPicker
            origin={form.pickup_lat != null ? { lat: form.pickup_lat, lng: form.pickup_lng } : null}
            destination={form.dest_lat != null ? { lat: form.dest_lat, lng: form.dest_lng } : null}
            originLabel="Pickup" destinationLabel="Destination" height={260}
            onPick={(which, p) => setForm((f) => which === "origin"
              ? { ...f, pickup: p.address, pickup_lat: p.lat, pickup_lng: p.lng }
              : { ...f, destination: p.address, dest_lat: p.lat, dest_lng: p.lng })}
          />

          <div className="space-y-1.5">
            <Label>Package Description <span className="text-rose-500">*</span></Label>
            <Textarea data-testid="delivery-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. A4 envelope with printed documents" className="rounded-xl" rows={2} />
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            <div className="space-y-1.5">
              <Label>Category <span className="text-rose-500">*</span></Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="delivery-category-select" className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{PACKAGE_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`category-${c}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Size <span className="text-rose-500">*</span></Label>
              <Select value={form.size} onValueChange={(v) => set("size", v)}>
                <SelectTrigger data-testid="delivery-size-select" className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{PACKAGE_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Approx. Weight <span className="text-rose-500">*</span></Label>
              <Input data-testid="delivery-weight-input" value={form.weight} onChange={(e) => set("weight", e.target.value)} placeholder="e.g. 1.5 kg" className="rounded-xl" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Preferred Delivery Date <span className="text-rose-500">*</span></Label>
            <Input type="date" data-testid="delivery-date-input" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} className="rounded-xl" />
          </div>

          <div className="space-y-1.5">
            <Label>Additional Notes</Label>
            <Textarea data-testid="delivery-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything the traveller should know" className="rounded-xl" rows={2} />
          </div>

          <div className="space-y-1.5">
            <Label>Package Photo <span className="text-slate-400 font-normal">(optional)</span></Label>
            <ImageUpload value={form.package_photo_id} onChange={(id) => set("package_photo_id", id)} purpose="package" testid="package-photo" hint="Only you and your connected traveller can see this photo." />
          </div>

          {!quote && form.category && form.weight && (form.pickup_lat == null || form.dest_lat == null) && (
            <p className="text-xs text-slate-400" data-testid="cost-hint">Pick both locations (search a city or tap the map) to see the estimated cost.</p>
          )}
          {quote && (
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4" data-testid="cost-estimate">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Estimated cost</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="cost-amount">₹{quote.amount}</p>
                </div>
                <div className="text-right text-xs text-slate-500">
                  <p>~{quote.distance_km} km · {quote.weight_kg} kg</p>
                  <p className="mt-1 inline-flex items-center gap-1 font-semibold text-emerald-600"><Wallet className="w-3.5 h-3.5" /> Cash on Delivery</p>
                </div>
              </div>
              <p className="mt-2 text-[11px] text-slate-400">Base ₹{quote.breakdown.base} + distance ₹{quote.breakdown.distance_fee} + weight ₹{quote.breakdown.weight_fee}, ×{quote.breakdown.category_multiplier} for {quote.breakdown.category}. Paid in cash to the traveller after delivery.</p>
            </div>
          )}

          <Button type="submit" data-testid="delivery-review-btn" className="w-full rounded-xl h-11">Review request <ArrowRight className="w-4 h-4" /></Button>
        </form>
      )}

      {step === 2 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm space-y-4" data-testid="delivery-review">
          <h3 className="font-semibold text-slate-900">Review your request</h3>
          {[
            ["Pickup", form.pickup], ["Destination", form.destination], ["Description", form.description],
            ["Category", form.category], ["Size", form.size], ["Weight", form.weight],
            ["Delivery date", form.delivery_date], ["Notes", form.notes || "—"],
            ["Payment", quote ? `₹${quote.amount} · Cash on Delivery` : "Cash on Delivery"],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
              <span className="text-sm text-slate-400">{k}</span>
              <span className="text-sm font-medium text-slate-800 text-right">{v}</span>
            </div>
          ))}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl flex-1" data-testid="delivery-back-btn"><ArrowLeft className="w-4 h-4" /> Edit</Button>
            <Button onClick={submit} disabled={loading} className="rounded-xl flex-1" data-testid="create-delivery-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Package Request"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
